//
// jQuery extensions
//

/* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */

import {stringToNumberWeak} from '@alias_root/src/framework/ods/convert';

$.fn.getWidth = function (): number {
	if (this.length !== 1) {
		throw new Error(getInvalidElementError(this, 'getWidth'));
	}

	const size = this.width();
	if (typeof size !== 'number') {
		throw new Error(`The method "width" expects to return a number but got a "${typeof size}"`);
	}

	return size;
};

$.fn.getHeight = function (): number {
	if (this.length !== 1) {
		throw new Error(getInvalidElementError(this, 'getHeight'));
	}

	const size = this.height();
	if (typeof size !== 'number') {
		throw new Error(`The method "height" expects to return a number but got a "${typeof size}"`);
	}

	return size;
};

$.fn.getOuterWidth = function (includeMargin: boolean = false): number {
	if (this.length !== 1) {
		throw new Error(getInvalidElementError(this, 'getOuterWidth'));
	}

	const size = this.outerWidth(includeMargin);
	if (typeof size !== 'number') {
		throw new Error(`The method "outerWidth" expects to return a number but got a "${typeof size}"`);
	}

	return size;
};

$.fn.getOuterHeight = function (includeMargin: boolean = false): number {
	if (this.length !== 1) {
		throw new Error(getInvalidElementError(this, 'getOuterHeight'));
	}

	const size = this.outerHeight(includeMargin);
	if (typeof size !== 'number') {
		throw new Error(`The method "outerHeight" expects to return a number but got a "${typeof size}"`);
	}

	return size;
};

$.fn.getOffset = function (): JQuery.Coordinates {
	if (this.length !== 1) {
		throw new Error(getInvalidElementError(this, 'getOffset'));
	}

	const offset = this.offset();
	if (offset === undefined || typeof offset.left !== 'number' || typeof offset.top !== 'number') {
		throw new Error('The method "offset" expects to return an object with a numeric top and left coordinate');
	}

	return offset as JQuery.Coordinates;
};

$.fn.getValueAsString = function (): string {
	if (this.length !== 1) {
		throw new Error(getInvalidElementError(this, 'getValueAsString'));
	}

	const val = this.val();

	if (typeof val !== 'string') {
		throw new Error(`The method "getValueAsString" expects the element to return a string but we got "${typeof val}"`);
	}

	return val;
};

$.fn.getValueAsStringWeak = function (): string | null {
	const val = this.length === 1 ? this.val() : null;

	return typeof val === 'string' ? val : null;
};

$.fn.getValueAsStrings = function (): string[] {
	if (this.length !== 1) {
		throw new Error(getInvalidElementError(this, 'getValueAsStrings'));
	}

	const val = this.val();

	if (!Array.isArray(val) || !val.every((o) => typeof o === 'string')) {
		throw new Error(`The method "getValueAsStrings" expects the element to return an array of strings but we got "${typeof val}"`);
	}

	return val;
};

/**
 * Get data as string associated with the matched elements.
 * If the data cannot be found or is not of type string or number, an exception  is thrown.
 * @param key - Name of the data stored.
 * @returns Returns value at named data store for the element.
 */
$.fn.getDataAsString = function (key: string): string {
	if (this.length !== 1) {
		throw new Error(getInvalidElementError(this, 'getDataAsString'));
	}

	const data: string | number = this.data(key);

	if (data === undefined) {
		throw new Error(`The method "getDataAsString" has no data attribute with name "${key}"`);
	}

	if (!['string', 'number'].includes(typeof data)) {
		throw new Error(
			`The method "getDataAsString" expects the element with name "${key}" to return a string or number but we got "${data}" with type "${typeof data}"`,
		);
	}

	return data.toString();
};

/**
 * Get data as string associated with the matched elements.
 * If the data cannot be found or is not of type string or number, null is returned.
 * @param key - Name of the data stored.
 * @returns Returns value at named data store for the element.
 */
$.fn.getDataAsStringWeak = function (key: string): string | null {
	if (this.length !== 1) {
		throw new Error(getInvalidElementError(this, 'getDataAsStringWeak'));
	}

	const data: string | number = this.data(key);

	if (data === undefined) {
		return null;
	}

	if (!['string', 'number'].includes(typeof data)) {
		return null;
	}

	return data.toString();
};

/**
 * Get data as number associated with the matched elements.
 * If the data cannot be found or cannot be converted to a number, an exception  is thrown.
 * @param key - Name of the data stored.
 * @returns Returns value at named data store for the element.
 */
$.fn.getDataAsNumber = function (key: string): number {
	if (this.length !== 1) {
		throw new Error(getInvalidElementError(this, 'getDataAsNumber'));
	}

	const data = this.data(key);

	if (data === undefined) {
		throw new Error(`The method "getDataAsNumber" has no data attribute with name "${key}"`);
	}

	const val = stringToNumberWeak(data);

	if (val === null) {
		throw new Error(
			`The method "getDataAsNumber" expects the element with name "${key}" to return a number but we got "${data}" with type "${typeof data}"`,
		);
	}

	return val;
};

/**
 * Get data as number associated with the matched elements.
 * If the data cannot be found or cannot be converted to a number, null is returned.
 * @param key - Name of the data stored.
 * @returns Returns value at named data store for the element.
 */
$.fn.getDataAsNumberWeak = function (key: string): number | null {
	if (this.length !== 1) {
		throw new Error(getInvalidElementError(this, 'getDataAsNumberWeak'));
	}

	const data = this.data(key);

	return stringToNumberWeak(data);
};

/**
 * Get the value of an attribute associated with the matched elements.
 * If the data cannot be found or is not of type string or number, an exception  is thrown.
 * @param key - The name of the attribute to get.
 * @returns Returns value at named data store for the element.
 */
$.fn.getAttrAsString = function (key: string): string {
	if (this.length !== 1) {
		throw new Error(getInvalidElementError(this, 'getAttrAsString'));
	}

	const data: string | number = this.attr(key);

	if (data === undefined) {
		throw new Error(`The method "getAttrAsString" has no data attribute with name "${key}"`);
	}

	if (!['string', 'number'].includes(typeof data)) {
		throw new Error(
			`The method "getAttrAsString" expects the element with name "${key}" to return a string or number but we got "${data}" with type "${typeof data}"`,
		);
	}

	return data.toString();
};

/**
 * Get the value of an attribute associated with the matched elements.
 * If the data cannot be found or is not of type string or number, null is returned.
 * @param key - The name of the attribute to get.
 * @returns Returns value at named data store for the element.
 */
$.fn.getAttrAsStringWeak = function (key: string): string | null {
	if (this.length !== 1) {
		throw new Error(getInvalidElementError(this, 'getAttrAsStringWeak'));
	}

	const data: string | number = this.attr(key);

	if (data === undefined) {
		return null;
	}

	if (!['string', 'number'].includes(typeof data)) {
		return null;
	}

	return data.toString();
};

/**
 * Get the value of an attribute associated with the matched elements.
 * If the data cannot be found or cannot be converted to a number, an exception  is thrown.
 * @param key - The name of the attribute to get.
 * @returns Returns value at named data store for the element.
 */
$.fn.getAttrAsNumber = function (key: string): number {
	if (this.length !== 1) {
		throw new Error(getInvalidElementError(this, 'getAttrAsNumber'));
	}

	const data = this.attr(key);

	if (data === undefined) {
		throw new Error(`The method "getAttrAsNumber" has no data attribute with name "${key}"`);
	}

	const val = stringToNumberWeak(data);

	if (val === null) {
		throw new Error(
			`The method "getAttrAsNumber" expects the element with name "${key}" to return a number but we got "${data}" with type "${typeof data}"`,
		);
	}

	return val;
};

/**
 * Get the value of an attribute associated with the matched elements.
 * If the data cannot be found or cannot be converted to a number, null is returned.
 * @param key - The name of the attribute to get.
 * @returns Returns value at named data store for the element.
 */
$.fn.getAttrAsNumberWeak = function (key: string): number | null {
	if (this.length !== 1) {
		throw new Error(getInvalidElementError(this, 'getAttrAsNumberWeak'));
	}

	const data = this.attr(key);

	return stringToNumberWeak(data);
};

/*
 *	return error when no unique element is selected
 */
function getInvalidElementError($el: JQuery, method: string) {
	return `The method "${method}" is only available on a unique element but identifies "${$el.length}". id="${$el.attr('id')}" class="${$el.attr('class')}"`;
}
